import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';

export interface AppConfig {
	production: boolean;
	apiUrl: string;
	redirectPingUrl: string;
	secondRegistrationForm: string;
	authClientId: string;
	loginPingUrl: string;
	version: string;
}

/**
 * Default configuration values used as fallback when config isn't loaded yet
 */
function getBaseURL(): string {
	return `${window.location.protocol}//${window.location.host}`;
}

// This config is only used in local, in DEV and PROD it is collected from OpenShift variables
export function getDefaultRedirectUrl(): string {
	return `${getBaseURL()}/dashboard`;
}

function getSecondRegistrationFormUrl(): string {
	return `${getBaseURL()}/registration-form`;
}

export const DEFAULT_CONFIG: AppConfig = {
	production: false,
	apiUrl: 'https://api-test.witglobal.net/rest/user-profile-we',
	redirectPingUrl: getDefaultRedirectUrl(),
	secondRegistrationForm: getSecondRegistrationFormUrl(),
	authClientId: '8472b70e-c2ab-4f55-963c-a2148b690daa',
	loginPingUrl: 'https://login-dev.witglobal.net',
	version: '1.0.0',
};

/**
 * Service to load and provide application configuration from assets/config.json
 * This allows for configuration to be injected via OpenShift ConfigMaps
 */
@Injectable({
	providedIn: 'root',
})
export class AppConfigService {
	private readonly http = inject(HttpClient);

	private config: AppConfig = DEFAULT_CONFIG;
	private configLoadPromise: Promise<AppConfig> | null = null;
	private configLoaded = false;

	/**
	 * Load configuration from assets/config.json
	 * Should be called during app initialization before other services
	 */
	public async loadConfig(): Promise<AppConfig> {
		if (this.configLoadPromise) {
			return this.configLoadPromise;
		}

		this.configLoadPromise = (async () => {
			try {
				// To avoid caching issues, append a cache buster to the URL
				const cacheBuster = `?v=${new Date().getTime()}`;
				// Try with relative path
				try {
					const data = await lastValueFrom(this.http.get<AppConfig>(`./assets/config.json${cacheBuster}`));
					this.config = { ...this.config, ...data };
				} catch (_e) {
					// Try with absolute path as fallback
					console.warn('Failed to load config from relative path, trying absolute path');
					const data = await lastValueFrom(this.http.get<AppConfig>(`/assets/config.json${cacheBuster}`));
					this.config = { ...this.config, ...data };
				}
			} catch (error) {
				console.error('Error loading configuration, using default values', error);
			} finally {
				this.configLoaded = true;
			}
			return this.config;
		})();

		return this.configLoadPromise;
	}

	/**
	 * Get the loaded configuration
	 * Will return default values if config hasn't been loaded yet
	 */
	public getConfig(): AppConfig {
		return this.config;
	}

	/**
	 * Check if configuration is already loaded
	 */
	public isLoaded(): boolean {
		return this.configLoaded;
	}
}
